// ============================================================================
// CASE DETAIL SECTION REGISTRY — Phase 9a (refactored for stepper-driven nav)
// ============================================================================
// Two structures replace the previous CASE_DETAIL_GROUPS:
//
//   • SECTION_REGISTRY     — flat catalogue of every render function, keyed
//                            by sectionCode. The stepper looks up here when
//                            a step is clicked, via STATE_TO_SECTION_MAP.
//
//   • SIDEBAR_GROUPS       — only cross-cutting groups that are NOT reached
//                            via the workflow stepper (Overview, Financial,
//                            Records, Audit). Lifecycle and per-type groups
//                            were moved out of the sidebar — they're now
//                            reached only by clicking a step in the
//                            <WorkflowChecklistRail>.
//
// STATE_TO_SECTION_MAP is the client-side fallback mapping from workflow
// state code → section code (+ optional filter). Once the backend ships
// `tbl_workflow_state.detail_section_code`, that field takes precedence
// (resolveStateSection should be updated then to read from the state row
//  first, falling back to this map for legacy definitions).

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { LitigationCase, LitigationCaseTypeCode } from 'api/types/litigationCase.types';
import { useRecsuitPermissions } from 'hooks/useRecsuitPermissions';

import SummarySection from './sections/SummarySection';
import CaseClosurePanel from './sections/CaseClosurePanel';
import CaseHoldPanel from './sections/CaseHoldPanel';
import WorkflowTimelinePanel from './sections/WorkflowTimelinePanel';
import GroupParticipationsSection from './sections/GroupParticipationsSection';
import CaseExpensesSection from './sections/CaseExpensesSection';
import CaseAssignmentsSection from './sections/CaseAssignmentsSection';
import CaseAccountsSection from './sections/CaseAccountsSection';
import CaseDetailsSection from './sections/CaseDetailsSection';
import CaseRespondentsSection from './sections/CaseRespondentsSection';

// ----------------------------------------------------------------------------
// Step context — populated when a section is opened via the progress stepper.
// Lets the section render a step-aware view (highlight latest record, show
// inline checklist + transition buttons for the current state, etc.).
// ----------------------------------------------------------------------------

export interface SectionStepContext {
  /** State code of the selected step (e.g. 'NOTICE_ISSUED'). */
  stateCode: string;
  /** Display name from the workflow definition. */
  stateDisplayName?: string;
  /** Step has been completed (workflow has moved past it). */
  isPast: boolean;
  /** Step equals the workflow instance's current state. */
  isCurrent: boolean;
  /** Step is reachable but not yet visited. */
  isFuture: boolean;
  /** Optional filter from STATE_TO_SECTION_MAP / detail_filter. */
  filter?: Record<string, unknown>;
}

export interface SectionRenderContext {
  kase: LitigationCase;
  permissions: ReturnType<typeof useRecsuitPermissions>;
  onMutated: () => void;
  /** Defined when the section was opened via a progress step click. */
  stepContext?: SectionStepContext;
  /**
   * Free-form params surfaced by the section opener (e.g. directed-open
   * from a case-group: { focusGroupId: '<uuid>' }). Sections read keys they
   * care about; unknown keys are ignored.
   */
  params?: Record<string, string | null | undefined>;
}

export type SectionRenderFn = (ctx: SectionRenderContext) => React.ReactNode;

// ----------------------------------------------------------------------------
// SectionMeta — every section, sidebar and step-reachable, defined here.
// ----------------------------------------------------------------------------

export interface SectionMeta {
  code: string;
  label: string;
  icon: keyof typeof IconSystem;
  /** Empty/undefined = applicable to all case types. */
  applicableCaseTypes?: LitigationCaseTypeCode[];
  /** Optional permission gate. */
  permission?: { module: string; action: string };
  /** Optional badge — count of records etc. Used by the sidebar. */
  badge?: (kase: LitigationCase) => number | string | null;
  render: SectionRenderFn;
}

// ----------------------------------------------------------------------------
// Phase 9a placeholder — used by every non-Summary section until later sub-
// phases land. Renders a step-context strip when opened via the stepper.
// ----------------------------------------------------------------------------

export function placeholderSection(
  label: string,
  comingIn: string,
  ctx?: SectionRenderContext,
): React.ReactNode {
  const sc = ctx?.stepContext;
  return React.createElement(
    'div',
    {
      style: {
        padding: '1.25rem',
        color: 'var(--text-secondary)',
        fontSize: '0.9rem',
        lineHeight: 1.6,
      },
    },
    sc && React.createElement(
      'div',
      {
        style: {
          padding: '0.55rem 0.85rem',
          marginBottom: '1rem',
          background: 'var(--bg-secondary)',
          borderLeft: '3px solid #2563eb',
          fontSize: '0.78rem',
          color: 'var(--text-primary)',
          borderRadius: 4,
        },
      },
      React.createElement('strong', null, 'Step context: '),
      sc.stateDisplayName ?? sc.stateCode,
      sc.isPast && ' · completed',
      sc.isCurrent && ' · current',
      sc.isFuture && ' · upcoming',
    ),
    React.createElement(
      'div',
      { style: { textAlign: 'center', padding: '2rem 0' } },
      React.createElement(IconSystem.info, {
        size: 32, style: { opacity: 0.3, marginBottom: '0.75rem' },
      }),
      React.createElement('div', { style: { fontWeight: 500, marginBottom: '0.25rem' } }, label),
      React.createElement('div', { style: { fontSize: '0.8rem' } }, `Coming soon — ${comingIn}`),
    ),
  );
}

// ----------------------------------------------------------------------------
// SECTION RENDERS — Phase 9a wires only `summary`. Other ids return
// placeholders. Phase 9b/c/d will replace the placeholder calls with real
// components.
// ----------------------------------------------------------------------------

const renderSummary: SectionRenderFn  = (ctx) => React.createElement(SummarySection, {
  kase: ctx.kase, onMutated: ctx.onMutated,
});
const renderClosure: SectionRenderFn  = (ctx) => React.createElement(CaseClosurePanel, {
  kase: ctx.kase, permissions: ctx.permissions, onMutated: ctx.onMutated,
});
const renderHold: SectionRenderFn     = (ctx) => React.createElement(CaseHoldPanel, {
  kase: ctx.kase, permissions: ctx.permissions, onMutated: ctx.onMutated,
});
const renderWorkflowTimeline: SectionRenderFn = (ctx) => React.createElement(WorkflowTimelinePanel, {
  kase: ctx.kase, onMutated: ctx.onMutated,
});
const renderGroupParticipations: SectionRenderFn = (ctx) => React.createElement(GroupParticipationsSection, {
  caseId: ctx.kase.id,
  focusGroupId: ctx.params?.focusGroupId,
});
const renderHearings: SectionRenderFn = (ctx) => placeholderSection('Hearings',                'Phase 9b', ctx);
const renderNotices: SectionRenderFn  = (ctx) => placeholderSection('Notices',                 'Phase 9b', ctx);
const renderOrders: SectionRenderFn   = (ctx) => placeholderSection('Orders',                  'Phase 9b', ctx);
const renderExpenses: SectionRenderFn = (ctx) => React.createElement(CaseExpensesSection, {
  kase: ctx.kase, onMutated: ctx.onMutated,
});
const renderAssignments: SectionRenderFn = (ctx) => React.createElement(CaseAssignmentsSection, {
  kase: ctx.kase, onMutated: ctx.onMutated,
});
const renderAccounts: SectionRenderFn = (ctx) => React.createElement(CaseAccountsSection, {
  kase: ctx.kase, onMutated: ctx.onMutated,
});
const renderCaseDetails: SectionRenderFn = (ctx) => React.createElement(CaseDetailsSection, {
  kase: ctx.kase, onMutated: ctx.onMutated,
});
const renderRespondents: SectionRenderFn = (ctx) => React.createElement(CaseRespondentsSection, {
  kase: ctx.kase, onMutated: ctx.onMutated,
});
const renderDocuments: SectionRenderFn= (ctx) => placeholderSection('Documents',               'Phase 9d', ctx);

// Per-type renders
const renderAward: SectionRenderFn        = (ctx) => placeholderSection('Award',          'Phase 9c', ctx);
const renderSection34: SectionRenderFn    = (ctx) => placeholderSection('§34 Challenge',  'Phase 9c', ctx);
const renderCheques: SectionRenderFn      = (ctx) => placeholderSection('Cheques',        'Phase 9c', ctx);
const renderComplaint: SectionRenderFn    = (ctx) => placeholderSection('Complaint',      'Phase 9c', ctx);
const renderPossession: SectionRenderFn   = (ctx) => placeholderSection('Possession',     'Phase 9c', ctx);
const renderAuction: SectionRenderFn      = (ctx) => placeholderSection('Auction',        'Phase 9c', ctx);
const renderAttachment: SectionRenderFn   = (ctx) => placeholderSection('Attachment',     'Phase 9c', ctx);
const renderSale: SectionRenderFn         = (ctx) => placeholderSection('Sale',           'Phase 9c', ctx);
const renderSatisfaction: SectionRenderFn = (ctx) => placeholderSection('Satisfaction',   'Phase 9c', ctx);

// ----------------------------------------------------------------------------
// SECTION_REGISTRY — flat catalogue. Stepper looks up by code. Sidebar groups
// reference codes by name. Add new sections here ONCE; reference from groups
// or the state→section map.
// ----------------------------------------------------------------------------

export const SECTION_REGISTRY: Record<string, SectionMeta> = {
  // --- Cross-cutting sections (sidebar) ---
  summary:    { code: 'summary',   label: 'Summary',   icon: 'file',       render: renderSummary },
  'case-details': { code: 'case-details', label: 'Case Details', icon: 'file', render: renderCaseDetails },
  accounts:   { code: 'accounts',  label: 'Accounts',  icon: 'list',       render: renderAccounts },
  respondents:{ code: 'respondents', label: 'Respondents', icon: 'users',   render: renderRespondents },
  assignments:{ code: 'assignments', label: 'Assignments', icon: 'user',
                render: renderAssignments },
  'group-participations':
              { code: 'group-participations', label: 'Group Participations', icon: 'tag',
                render: renderGroupParticipations },

  // --- Case Lifecycle (Phase 7) ---
  'workflow-timeline':
              { code: 'workflow-timeline', label: 'Workflow Timeline', icon: 'clock', render: renderWorkflowTimeline },
  closure:    { code: 'closure',   label: 'Closure',     icon: 'check',    render: renderClosure },
  hold:       { code: 'hold',      label: 'Hold History', icon: 'clock',   render: renderHold },
  expenses:   { code: 'expenses',  label: 'Expenses',  icon: 'dollarSign',
                permission: { module: 'expense', action: 'view' },
                render: renderExpenses },
  documents:  { code: 'documents', label: 'Documents', icon: 'folder',     render: renderDocuments },

  // --- Step-reachable: Lifecycle (no longer in sidebar) ---
  hearings:   { code: 'hearings',  label: 'Hearings',  icon: 'calendar',
                permission: { module: 'litigation-case', action: 'view_hearings' },
                render: renderHearings },
  notices:    { code: 'notices',   label: 'Notices',   icon: 'mail',       render: renderNotices },
  orders:     { code: 'orders',    label: 'Orders',    icon: 'file',       render: renderOrders },

  // --- Step-reachable: Arbitration ---
  award:        { code: 'award',        label: 'Award',         icon: 'check',
                  applicableCaseTypes: ['ARBITRATION'], render: renderAward },
  'section-34': { code: 'section-34',   label: '§34 Challenge', icon: 'lock',
                  applicableCaseTypes: ['ARBITRATION'], render: renderSection34 },

  // --- Step-reachable: NI-Act §138 ---
  cheques:    { code: 'cheques',   label: 'Cheques',   icon: 'file',
                applicableCaseTypes: ['NI_ACT_138'], render: renderCheques },
  complaint:  { code: 'complaint', label: 'Complaint', icon: 'edit',
                applicableCaseTypes: ['NI_ACT_138'], render: renderComplaint },

  // --- Step-reachable: SARFAESI ---
  possession: { code: 'possession', label: 'Possession', icon: 'home',
                applicableCaseTypes: ['SARFAESI'], render: renderPossession },
  auction:    { code: 'auction',    label: 'Auction',    icon: 'tag',
                applicableCaseTypes: ['SARFAESI'], render: renderAuction },

  // --- Step-reachable: Execution ---
  attachment:   { code: 'attachment',   label: 'Attachment',   icon: 'link',
                  applicableCaseTypes: ['EP'], render: renderAttachment },
  sale:         { code: 'sale',         label: 'Sale',         icon: 'tag',
                  applicableCaseTypes: ['EP'], render: renderSale },
  satisfaction: { code: 'satisfaction', label: 'Satisfaction', icon: 'check',
                  applicableCaseTypes: ['EP'], render: renderSatisfaction },
};

// ----------------------------------------------------------------------------
// SIDEBAR_GROUPS — cross-cutting only. Lifecycle and per-type groups were
// removed from the sidebar; they're reached via <WorkflowChecklistRail>.
// ----------------------------------------------------------------------------

export interface SidebarGroup {
  id: string;
  label: string;
  defaultExpanded?: boolean;
  sectionCodes: string[];
  /** Case types for which this whole group is hidden (its content lives
   *  elsewhere for them — e.g. arbitration surfaces Case Setup inside the
   *  Filing-stage tabs, so the sidebar group is suppressed). ADR-0002 §2.8. */
  hideForCaseTypes?: LitigationCaseTypeCode[];
}

/** Resolved sidebar group — sections inlined and filtered by case type + permission. */
export interface ResolvedSidebarGroup {
  id: string;
  label: string;
  defaultExpanded?: boolean;
  sections: SectionMeta[];
}

export const SIDEBAR_GROUPS: SidebarGroup[] = [
  { id: 'overview',  label: 'Overview',       defaultExpanded: true, sectionCodes: ['summary', 'assignments', 'group-participations'] },
  // Case Setup (ADR-0001) — litigation-detail sections. Hidden for case types
  // that surface these inside their Filing-stage tabs (ADR-0002 §2.8):
  // arbitration renders Details + Accounts & Respondents as Filing tabs.
  { id: 'case-setup', label: 'Case Setup',    defaultExpanded: true, sectionCodes: ['case-details', 'accounts', 'respondents'], hideForCaseTypes: ['ARBITRATION', 'CIVIL_SUIT', 'NI_ACT_138'] },
  { id: 'lifecycle', label: 'Case Lifecycle', defaultExpanded: true, sectionCodes: ['workflow-timeline', 'closure', 'hold'] },
  { id: 'financial', label: 'Financial',                             sectionCodes: ['expenses'] },
  { id: 'records',   label: 'Records',                               sectionCodes: ['documents'] },
];

// ----------------------------------------------------------------------------
// STATE_TO_SECTION_MAP — client-side fallback. State code → section code
// (+ optional filter passed to the section via stepContext.filter).
//
// When backend ships `tbl_workflow_state.detail_section_code`, prefer that
// value over this map — see resolveStateSection() below for the seam.
//
// Keys are the workflow state codes seeded into ewf.tbl_workflow_state. Add
// entries as new workflow definitions are seeded.
// ----------------------------------------------------------------------------

export interface StateSectionMapping {
  sectionCode: string;
  filter?: Record<string, unknown>;
}

export const STATE_TO_SECTION_MAP: Record<string, StateSectionMapping> = {
  // NI-Act §138
  CHEQUE_RETURNED:        { sectionCode: 'cheques',   filter: { highlightLatest: true } },
  MEMO_RECEIVED:          { sectionCode: 'cheques',   filter: { highlightLatest: true } },
  ASSIGNED_TO_ADVOCATE:   { sectionCode: 'documents', filter: { type: 'engagement' } },
  NOTICE_ISSUED:          { sectionCode: 'notices',   filter: { highlightLatest: true } },
  NOTICE_ACKED:           { sectionCode: 'notices',   filter: { highlightLatest: true } },
  PETITION_FILED:         { sectionCode: 'complaint' },
  COGNIZANCE:             { sectionCode: 'complaint' },
  HEARINGS_IN_PROGRESS:   { sectionCode: 'hearings' },
  JUDGEMENT:              { sectionCode: 'orders' },

  // Arbitration
  ARBITRATOR_APPOINTED:   { sectionCode: 'documents', filter: { type: 'appointment_letter' } },
  STATEMENT_OF_CLAIM:     { sectionCode: 'documents', filter: { type: 'soc' } },
  ARBITRATION_HEARINGS:   { sectionCode: 'hearings' },
  AWARD_PASSED:           { sectionCode: 'award' },
  SECTION_34_CHALLENGE:   { sectionCode: 'section-34' },

  // SARFAESI
  POSSESSION_TAKEN:       { sectionCode: 'possession' },
  AUCTION_NOTICE:         { sectionCode: 'auction' },
  AUCTION_HELD:           { sectionCode: 'auction' },

  // Execution
  ATTACHMENT_ORDER:       { sectionCode: 'attachment' },
  SALE_DEED:              { sectionCode: 'sale' },
  SATISFIED:              { sectionCode: 'satisfaction' },
};

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

/**
 * Filter and resolve sidebar groups for a given case + permissions context.
 * Sections that don't apply to the case type or that the user lacks
 * permission for are dropped; empty groups disappear.
 */
export function visibleSidebarGroups(
  kase: LitigationCase,
  permissions: ReturnType<typeof useRecsuitPermissions>,
): ResolvedSidebarGroup[] {
  return SIDEBAR_GROUPS
    .filter(g => !g.hideForCaseTypes?.includes(kase.caseTypeCode))
    .map<ResolvedSidebarGroup>(g => ({
      id: g.id,
      label: g.label,
      defaultExpanded: g.defaultExpanded,
      sections: g.sectionCodes
        .map(code => SECTION_REGISTRY[code])
        .filter((s): s is SectionMeta => Boolean(s))
        .filter(s => {
          if (s.applicableCaseTypes && !s.applicableCaseTypes.includes(kase.caseTypeCode)) return false;
          if (s.permission && !permissions.hasAction(s.permission.module, s.permission.action)) return false;
          return true;
        }),
    }))
    .filter(g => g.sections.length > 0);
}

/**
 * Resolve a workflow state code to the section that should render in the
 * right pane. Returns null if no mapping exists — caller should render an
 * inline placeholder.
 */
export function resolveStateSection(stateCode: string): SectionMeta | null {
  const mapping = STATE_TO_SECTION_MAP[stateCode];
  if (!mapping) return null;
  return SECTION_REGISTRY[mapping.sectionCode] ?? null;
}

/** Optional filter for the resolved section, passed via stepContext.filter. */
export function resolveStateFilter(stateCode: string): Record<string, unknown> | undefined {
  return STATE_TO_SECTION_MAP[stateCode]?.filter;
}
